import React, { useState, useEffect } from 'react';
import { getAgeTier, getPortalLabel, getPortalStyle } from '../utils/ageUtils';
import { RoomData, Player } from '../types';
import Avatar from './Avatar';
import { getZoomEmbedUrl } from '../services/zoomService';
import SchedulePopup, { NextRitualWidget } from './SchedulePopup';
import ScheduleSidebar from './ScheduleSidebar';
import SpiritGuide from './SpiritGuide';
import OccupantList from './OccupantList';
import { db, collection, query, safeOnSnapshot, doc, updateDoc, setDoc } from '../firebaseConfig';
import GuardianBoard from './GuardianBoard';
import { isStaff, isStudent } from '../utils/roles';
import DailyJournal from './DailyJournal';
import type { AccessContext } from '../utils/access';

interface RoomProps {
  room: RoomData;
  me: Player;
  occupants: Player[];
  guideMessage: string;
  onExit: () => void;
  onFloorClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onPortalToggle: (active: boolean) => void;
  onOpenForge?: (roomId: string) => void;
  onNavigateToRoom?: (roomId: string) => void;
  onPlayerClick?: (player: Player) => void;
  accessCtx: AccessContext;
}

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') {
    return val.da || val.en || val.nb || Object.values(val).find(v => typeof v === 'string') || JSON.stringify(val);
  }
  return String(val);
};


const Room: React.FC<RoomProps> = ({ room, me, occupants, guideMessage, onExit, onFloorClick, onPortalToggle, onOpenForge, onPlayerClick, onNavigateToRoom, accessCtx }) => {
  const [showZoomPortal, setShowZoomPortal] = useState(false);
  const [zoomMinimized, setZoomMinimized] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showSpiritGuide, setShowSpiritGuide] = useState(false);
  const [showOccupantList, setShowOccupantList] = useState(false);
  const [showScheduleSidebar, setShowScheduleSidebar] = useState(false);
  const [showGuardianBoard, setShowGuardianBoard] = useState(false);
  const [activeBoardOwner, setActiveBoardOwner] = useState<{id: string; guardianId: string; name: string; subjectName?: string} | null>(null);
  const [previewTier, setPreviewTier] = useState<1|2|3|4|5|6|null>(null);
  const [nextRitualTime, setNextRitualTime] = useState<string | undefined>();
  const [guardianBoards, setGuardianBoards] = useState<any[]>([]);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [hallsForBoard, setHallsForBoard] = useState<any[]>([]);
  const [newBoardLinkedHallId, setNewBoardLinkedHallId] = useState('');
  const [newBoardLinkedSubjectId, setNewBoardLinkedSubjectId] = useState('');


  // DailyJournal
  const [showJournal, setShowJournal] = useState(false);
  const [journalMode, setJournalMode] = useState<'exit' | 'manual'>('manual');
  const [visitStart] = useState(Date.now());
  const [boardReads, setBoardReads] = useState<Record<string, number>>({});

  useEffect(() => {
    const q = query(collection(db, `room_notices/${room.id}/boards`));
    const unsub = safeOnSnapshot(q, (snap: any) => {
      const boards: any[] = [];
      snap.forEach((d: any) => boards.push({ ...d.data(), id: d.id }));
      setGuardianBoards(boards);

    });
    return () => unsub();
  }, [room.id]);
  // 18-8-2026: laest-markering flyttet fra localStorage til Firestore, saa den
  // gaelder paa tvaers af enheder OG kan laeses af Halls' overblik.
  // Sti: room_notices/{rum}/boards/{vogter}/reads/{elev} = { lastSeen }
  useEffect(() => {
    if (!me.id || !guardianBoards.length) return;
    let afbrudt = false;
    (async () => {
      try {
        const { doc: d3, getDoc } = await import('firebase/firestore');
        const par = await Promise.all(guardianBoards.map(async (b: any) => {
          try {
            const sn = await getDoc(d3(db, `room_notices/${room.id}/boards/${b.id}/reads`, me.id));
            const v = sn.exists() ? Number((sn.data() || {}).lastSeen) : 0;
            return [b.id, isFinite(v) ? v : 0] as [string, number];
          } catch (e) { return [b.id, 0] as [string, number]; }
        }));
        if (!afbrudt) setBoardReads(Object.fromEntries(par));
      } catch (e) { /* reads utilgaengelig - localStorage-fallback bruges */ }
    })();
    return () => { afbrudt = true; };
  }, [room.id, me.id, guardianBoards.length]);
  const markBoardRead = async (roomId: string, boardId: string, ts: number) => {
    setBoardReads(prev => ({ ...prev, [boardId]: ts }));
    try {
      const { doc: d3, setDoc } = await import('firebase/firestore');
      await setDoc(d3(db, `room_notices/${roomId}/boards/${boardId}/reads`, me.id), { lastSeen: ts, userId: me.id }, { merge: true });
    } catch (e) { console.warn('markBoardRead fejlede:', e); }
  };

  const hasUnread = (boardId: string) => {
    const board = guardianBoards.find(b => b.id === boardId);
    if (!board) return false;
    const fsSeen = boardReads[boardId];
    const lsSeen = parseInt(localStorage.getItem(`board_seen_${room.id}_${boardId}`) || '0');
    const lastSeen = (typeof fsSeen === 'number' && fsSeen > 0) ? fsSeen : lsSeen;
    return board.lastUpdated > lastSeen;
  };

  const activatePortal = async () => {
    setIsActivating(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.warn("Hardware adgang nægtet:", err);
    }
    setTimeout(() => {
      setShowZoomPortal(true);
      onPortalToggle(true);
      setIsActivating(false);
    }, 1500);
  };

  const togglePortalActive = async () => {
    const { doc: firestoreDoc, updateDoc } = await import('firebase/firestore');
    const newStatus = !room.isPortalActive;
    await updateDoc(firestoreDoc(db, 'rooms', room.id), {
      isPortalActive: newStatus,
      portalOverride: newStatus ? 'on' : 'off',
    });
    if (room.classroomId) {
      await updateDoc(firestoreDoc(db, 'classrooms', room.classroomId), { isPortalActive: newStatus }).catch(() => {});
    }
  };
  const handleClosePortal = () => {
    setShowZoomPortal(false);
    setShowSchedule(false);
    setShowSpiritGuide(false);
    onPortalToggle(false);
  };

  const openZoomExternal = () => {
    const url = room.zoomMeetingId.startsWith('http') ? room.zoomMeetingId : `https://zoom.us/j/${room.zoomMeetingId.replace(/\s/g, '')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleExitWithJournal = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isStudent(me.role)) {
      setJournalMode('exit');
      setShowJournal(true);
    } else {
      onExit();
    }
  };

  const handleJournalClose = () => {
    setShowJournal(false);
    if (journalMode === 'exit') onExit();
  };

  const backgroundUrl = room.roomBackgroundUrl || 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?q=80&w=2000';

  const isMobile = window.innerWidth < 768;
  const crowdSize = occupants.length;
  let crowdFactor = 1.0;
  if (isMobile) {
    if (crowdSize > 15) crowdFactor = 0.45;
    else if (crowdSize > 8) crowdFactor = 0.6;
    else crowdFactor = 0.8;
  } else {
    if (crowdSize > 30) crowdFactor = 0.6;
    else if (crowdSize > 15) crowdFactor = 0.8;
  }

  const zoomUrl = getZoomEmbedUrl(room.zoomMeetingId, me.wizardName || me.name || "Eventyrer");

  return (
    <div
      className="relative w-full h-full overflow-hidden cursor-crosshair bg-black"
      onClick={(e) => (!showZoomPortal || zoomMinimized) && onFloorClick(e)}
    >
      <img
        src={backgroundUrl || undefined}
        className={`absolute inset-0 w-full h-full object-cover object-center transition-all duration-1000 ${(showZoomPortal && !zoomMinimized) ? 'opacity-5 scale-110 grayscale' : 'opacity-40 scale-105'}`}
        alt=""
      />

      {(!showZoomPortal || zoomMinimized) && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <AvatarWrapper player={me} isMe={true} crowdFactor={crowdFactor} onPlayerClick={onPlayerClick} />
          {occupants.filter(o => o.id !== me.id).map(player => (
            <AvatarWrapper key={player.id} player={player} isMe={false} crowdFactor={crowdFactor} onPlayerClick={onPlayerClick} />
          ))}
        </div>
      )}

      {/* SPIRIT ORB */}
      {(!showZoomPortal || zoomMinimized) && (
        <div
          className="absolute left-6 bottom-32 md:top-10 md:left-[350px] z-[90] cursor-pointer pointer-events-auto group"
          onClick={(e) => { e.stopPropagation(); setShowSpiritGuide(!showSpiritGuide); }}
        >
          <div className="absolute inset-0 bg-cyan-400/30 blur-3xl rounded-full group-hover:bg-cyan-400/50 animate-pulse scale-150 transition-all duration-1000" />
          <div className="relative flex flex-col items-center">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-white via-cyan-400 to-cyan-800 rounded-full shadow-[0_0_30px_rgba(34,211,238,0.8)] border-2 border-cyan-200/50 flex items-center justify-center animate-bounce" style={{ animationDuration: '4s' }}>
              <div className="w-4 h-4 md:w-6 md:h-6 bg-white rounded-full blur-[2px] animate-pulse opacity-80" />
            </div>
            <div className="mt-2 bg-black/60 px-3 py-1 rounded-full border border-cyan-400/30 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest">Spirit Guide</span>
            </div>
          </div>
        </div>
      )}

      {isActivating && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-indigo-950/60 backdrop-blur-md">
          <div className="relative flex flex-col items-center">
            <div className="w-16 h-16 md:w-24 md:h-24 border-4 border-yellow-500/10 border-t-yellow-500 rounded-full animate-spin mb-6" />
            <p className="text-yellow-500 font-cinzel text-xs md:text-sm animate-pulse tracking-[0.3em]">Åbner Portalen...</p>
          </div>
        </div>
      )}

      {/* ZOOM PORTAL MODAL */}
      {showZoomPortal && (
        <div className={zoomMinimized
          ? "fixed bottom-4 right-4 z-[200] w-[380px] h-[240px] rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-500/40 flex flex-col"
          : "fixed inset-0 z-[200] bg-black flex flex-col overflow-hidden"}>
          {/* TOP BAR */}
          <div className="bg-indigo-950/98 backdrop-blur-xl py-2 px-3 border-b border-yellow-600/30 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-500 rounded flex items-center justify-center font-cinzel text-indigo-950 font-bold text-[10px]">N</div>
              {!zoomMinimized && <span className="font-cinzel text-yellow-500 text-[10px] tracking-widest truncate max-w-[200px]">{s(room.name)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setZoomMinimized(!zoomMinimized)}
                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all"
                title={zoomMinimized ? 'Udvid' : 'Formindsk'}
              >
                {zoomMinimized ? '⛶' : '⊡'}
              </button>
              <button
                onClick={openZoomExternal}
                className="bg-white/10 hover:bg-yellow-500 hover:text-black text-white/60 px-3 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all"
                title="Åbn i Zoom App"
              >🚀</button>
              <button
                onClick={handleClosePortal}
                className="bg-red-900/40 hover:bg-red-600 text-white/60 hover:text-white px-3 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all"
              >✕ Luk</button>
            </div>
          </div>
          {/* IFRAME */}
          <div className="flex-1 bg-black relative overflow-hidden">
            {zoomUrl ? (
              <iframe src={zoomUrl} className="w-full h-full border-none" allow="camera *; microphone *; display-capture *; autoplay; encrypted-media; fullscreen" title="Zoom Portal" />
            ) : (
              <div className="flex items-center justify-center h-full text-indigo-400 font-cinzel italic text-xs">Portnøgle mangler...</div>
            )}
          </div>
        </div>
      )}

      {/* INFO BLOK */}
      {(!showZoomPortal || zoomMinimized) && (
        <div className="absolute top-4 left-4 z-[80] w-[calc(100%-5rem)] md:w-80 pointer-events-none">
          <div className="bg-[#0f0d1a]/95 border border-indigo-400/30 p-3 md:p-5 rounded-2xl md:rounded-[2rem] backdrop-blur-xl shadow-2xl pointer-events-auto space-y-2 md:space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="text-sm md:text-xl text-white font-cinzel truncate flex-1 font-bold">{s(room.name)}</h2>
              <button onClick={(e) => { e.stopPropagation(); setShowOccupantList(true); }} className="ml-2 bg-indigo-500/30 hover:bg-indigo-500/60 border border-indigo-400/50 px-2.5 py-1 rounded-full flex items-center gap-1.5 transition-all">
                <span className="text-xs text-yellow-400">👥</span>
                <span className="text-xs font-black text-white">{occupants.length}</span>
              </button>
            </div>
            {(() => {
              const isGuardian = isStaff(me.role);
              const tier = isGuardian && !previewTier ? 3 : (previewTier || getAgeTier(me.ageGroup));
              const portalActive = room.isPortalActive && room.zoomMeetingId;
              return (
                <div className="space-y-1.5">
                  <button onClick={(e) => { e.stopPropagation(); activatePortal(); }} disabled={!portalActive && !isGuardian} className={getPortalStyle(tier, !!portalActive)}>
                    {isGuardian ? (portalActive ? '🔮 Portal aktiv — Åbn Zoom' : '🔮 Tænd Portnøgle') : getPortalLabel(tier, !!portalActive, nextRitualTime)}
                  </button>
                  {isGuardian && room.zoomMeetingId && (
                    <button onClick={(e) => { e.stopPropagation(); togglePortalActive(); }}
                      className={`w-full py-1.5 rounded-xl text-[10px] font-black uppercase border transition-all flex items-center justify-center gap-1.5 ${room.isPortalActive ? 'bg-red-900/30 border-red-500/40 text-red-300 hover:bg-red-800/50' : 'bg-green-900/20 border-green-500/30 text-green-400 hover:bg-green-900/40'}`}>
                      {room.isPortalActive ? '🔴 Sluk portal for elever' : '🟢 Tænd portal for elever'}
                    </button>
                  )}
                </div>
              );
            })()}
            <NextRitualWidget roomId={room.id} currentUser={me} onClick={() => setShowSchedule(true)} onNextRitual={setNextRitualTime} allRooms={Object.values((accessCtx as any)?.rooms || {}) as any[]} />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={(e) => { e.stopPropagation(); setShowSchedule(true); }} className="bg-indigo-800/60 hover:bg-indigo-700/80 text-white py-2 md:py-2.5 rounded-xl font-bold text-xs border border-indigo-500/40 transition-all flex items-center justify-center gap-1.5">
                <span>📅</span><span>Skema</span>
              </button>
              <button onClick={handleExitWithJournal} className="bg-red-900/40 hover:bg-red-800/60 text-red-200 py-2 md:py-2.5 rounded-xl font-bold text-xs border border-red-500/30 transition-all flex items-center justify-center gap-1.5" title="Tilbage til campus-forhallen">
                <span>🚪</span><span>Forlad rum</span>
              </button>
            </div>
            {(isStudent(me.role) || previewTier !== null) && (
              <button onClick={(e) => { e.stopPropagation(); setJournalMode('manual'); setShowJournal(true); }} className="w-full bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200 py-2 md:py-2.5 rounded-xl font-bold text-xs border border-yellow-500/40 transition-all flex items-center justify-center gap-1.5">
                <span>📜</span><span>Dagens Dagbog</span>
              </button>
            )}
            {(isStaff(me.role)) && (
              <div className="border-t border-white/10 pt-2 space-y-1">
                <p className="text-[8px] text-white/40 uppercase font-bold tracking-widest">Forhåndsvisning:</p>
                <div className="grid grid-cols-6 gap-1">
                  {([1,2,3,4,5,6] as const).map(t => (
                    <button key={t} onClick={(e) => { e.stopPropagation(); setPreviewTier(previewTier === t ? null : t); }} className={`py-1 rounded text-[7px] font-black uppercase transition ${previewTier === t ? 'bg-yellow-500 text-black' : 'bg-white/10 text-white/40 hover:bg-white/20'}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {previewTier && <button onClick={() => setPreviewTier(null)} className="w-full text-[8px] text-yellow-400/60 uppercase font-bold">✕ Afslut</button>}
              </div>
            )}
          </div>
        </div>
      )}

      {showSpiritGuide && (!showZoomPortal || zoomMinimized) && (
        <SpiritGuide roomName={s(room.name)} onClose={() => setShowSpiritGuide(false)} />
      )}

      {(!showZoomPortal || zoomMinimized) && showGuardianBoard && activeBoardOwner && (
        <GuardianBoard
          roomId={room.id}
          currentUser={me}
          boardOwnerId={activeBoardOwner.id}
          boardOwnerName={activeBoardOwner.name}
          subjectName={activeBoardOwner.subjectName}
          classroomId={room.classroomId}
          classroomIds={room.classroomIds}
          accessCtx={accessCtx}
          occupants={occupants.filter(o => isStaff(o.role))}
          onClose={() => { setShowGuardianBoard(false); setActiveBoardOwner(null); }}
        />
      )}


      {/* GUARDIAN BARRE */}
      {(!showZoomPortal || zoomMinimized) && (() => {
        const isGuardianUser = isStaff(me.role);
        // 18-8-2026: vogteren ser sine EGNE skjulte tavler (nedtonet, med 'vis igen').
        // Foer var der ingen vej tilbage: en tavle skjult ved et uheld var usynlig
        // for baade elever OG vogteren selv - een laa gemt i fire maaneder med en
        // velkomstbesked eleverne aldrig saa. Elever ser stadig kun de synlige.
        const myBoards = guardianBoards.filter(b => b.guardianId === me.id);
        const otherBoards = guardianBoards.filter(b => b.guardianId !== me.id && b.isVisible !== false);

        return (
          <div className="absolute bottom-56 md:bottom-20 left-1/2 -translate-x-1/2 z-[80] flex flex-wrap gap-2 justify-center pointer-events-auto max-w-[80vw]">
            {isGuardianUser && (
              <button
                onClick={async () => {
                if (hallsForBoard.length === 0) {
                  // Phase 3: hent classrooms + flat subjects collection i parallel
                  const { getDocs, collection: col } = await import('firebase/firestore');
                  const [classSnap, subjSnap] = await Promise.all([
                    getDocs(col(db, 'classrooms')),
                    getDocs(col(db, 'subjects')),
                  ]);
                  const allSubjects = subjSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
                  setHallsForBoard(classSnap.docs.map(d => {
                    const cData = d.data() as any;
                    // Foretraek flat collection; fallback til gammel nested struktur
                    const flatForClass = allSubjects.filter((s: any) => s.classroomId === d.id);
                    const subjects = flatForClass.length > 0 ? flatForClass : (cData.subjects || []);
                    return { id: d.id, name: cData.name || '', subjects };
                  }));
                }
                setShowCreateBoard(true);
              }}
                className="flex items-center gap-2 bg-yellow-800/70 border-2 border-yellow-600/70 hover:border-yellow-400 px-4 py-2.5 rounded-xl backdrop-blur-md transition-all shadow-lg"
              >
                <span className="text-sm">📜</span>
                <span className="text-xs font-black uppercase text-yellow-300 tracking-widest">+ Ny Vogterrulle</span>
              </button>
            )}
            {[...myBoards, ...otherBoards].map(board => {
              const unread = hasUnread(board.id);
              const isMyBoard = board.guardianId === me.id;
              return (
                <button
                  key={board.id}
                  onClick={() => { const _now = Date.now(); localStorage.setItem(`board_seen_${room.id}_${board.id}`, String(_now)); markBoardRead(room.id, board.id, _now); setActiveBoardOwner({ id: board.guardianId, guardianId: board.guardianId, name: board.guardianName, subjectName: board.subjectTitle }); setShowGuardianBoard(true); }}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl backdrop-blur-md transition-all shadow-lg border-2 ${unread ? 'bg-yellow-900/40 border-yellow-400 shadow-[0_0_18px_rgba(250,204,21,.45)]' : (isMyBoard ? 'bg-indigo-900/90 border-indigo-400' : 'bg-black/80 border-indigo-500/60 hover:border-indigo-300')}`}
                >
                  {/* 18-8-2026: prikken var 12px i hjoernet og blev overset. Nu gloeder
                      hele tavlen + et laesbart badge, saa eleven ikke gaar forbi nyt. */}
                  {unread && (
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-yellow-400 text-black rounded-full border-2 border-black text-[9px] font-black uppercase tracking-widest animate-pulse shadow-lg">
                      Nyt
                    </span>
                  )}
                  <span className="text-sm">📜</span>
                  <div className={'text-left ' + (board.isVisible === false ? 'opacity-50' : '')}>
                    <p className={`text-xs font-black uppercase tracking-widest ${isMyBoard ? 'text-yellow-300' : 'text-white'}`}>{board.subjectTitle || 'Vogterens Rulle'}</p>
                    <p className="text-[10px] uppercase text-white/80">{board.isVisible === false ? 'Skjult for eleverne' : board.guardianName}</p>
                  </div>
                  {isMyBoard && isGuardianUser && (
                    board.isVisible === false ? (
                      <span
                        onClick={async (e) => { e.stopPropagation(); await updateDoc(doc(db, `room_notices/${room.id}/boards/${board.id}`), { isVisible: true }); }}
                        title="Skjult for eleverne — klik for at vise den igen"
                        className="ml-1 text-amber-400/80 hover:text-amber-300 text-[9px] font-black uppercase cursor-pointer whitespace-nowrap"
                      >👁 Vis igen</span>
                    ) : (
                      <span
                        onClick={async (e) => { e.stopPropagation(); await updateDoc(doc(db, `room_notices/${room.id}/boards/${board.id}`), { isVisible: false }); }}
                        title="Skjul for eleverne. Rullen arkiverer sig selv — du behøver ikke skjule den for at rydde op."
                        className="ml-1 text-white/40 hover:text-red-400 text-[10px] font-black cursor-pointer"
                      >✕</span>
                    )
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* NY VOGTERRULLE DIALOG */}
      {showCreateBoard && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#1a1510] border-2 border-yellow-800/40 rounded-2xl p-6 w-96 space-y-4 max-h-[80vh] overflow-y-auto no-scrollbar">
            <h3 className="font-cinzel text-yellow-600 uppercase tracking-widest text-sm">Ny vogterrulle</h3>
            <input
              id="newBoardTitle"
              autoFocus
              placeholder="Fx Dansk · Dragons 7-8"
              className="w-full bg-black/40 border border-yellow-800/30 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-yellow-600"
            />
            {/* KOBL TIL FAGDØR (VALGFRIT) */}
            <div className="space-y-2">
              <p className="text-[9px] text-white/40 font-black uppercase">🚪 Kobl til AcademyHalls fagdør (valgfrit)</p>
              <p className="text-[9px] text-white/25 italic">Beskeder i rullen synkroniseres med fagdøren i Halls</p>
              {newBoardLinkedSubjectId ? (
                <div className="flex items-center justify-between bg-blue-900/20 border border-blue-500/30 rounded-lg px-3 py-2">
                  <span className="text-[9px] text-blue-300 font-black uppercase">
                    {hallsForBoard.find(h => h.id === newBoardLinkedHallId)?.subjects?.find((s: any) => s.id === newBoardLinkedSubjectId)?.name?.da || 'Fagdør valgt'}
                  </span>
                  <button onClick={() => { setNewBoardLinkedSubjectId(''); setNewBoardLinkedHallId(''); }}
                    className="text-white/30 hover:text-red-400 text-xs">✕</button>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1 no-scrollbar">
                  {hallsForBoard.map((hall: any) => (
                    <div key={hall.id}>
                      <p className="text-[8px] text-white/25 font-black uppercase px-1 mt-2 mb-1">{hall.name}</p>
                      {(hall.subjects || []).filter((s: any) => s.isActive !== false).map((subj: any) => {
                        const subjName = typeof subj.name === 'string' ? subj.name : subj.name?.da || subj.name?.en || 'Fagdør';
                        return (
                          <button key={subj.id}
                            onClick={() => { setNewBoardLinkedSubjectId(subj.id); setNewBoardLinkedHallId(hall.id); }}
                            className="w-full text-left text-[9px] px-2 py-1.5 rounded bg-white/5 hover:bg-blue-900/30 text-white/60 hover:text-blue-300 border border-transparent hover:border-blue-500/30 transition-all mb-0.5">
                            {subjName}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t border-white/10">
              <button onClick={() => { setShowCreateBoard(false); setNewBoardLinkedHallId(''); setNewBoardLinkedSubjectId(''); }}
                className="text-yellow-800 text-[9px] font-black uppercase">Annuller</button>
              <button
                onClick={async () => {
                  const title = (document.getElementById('newBoardTitle') as HTMLInputElement)?.value?.trim();
                  if (!title) return;
                  await setDoc(doc(db, `room_notices/${room.id}/boards`, me.id), {
                    guardianId: me.id,
                    guardianName: me.wizardName || me.name,
                    subjectTitle: title,
                    isVisible: true,
                    lastUpdated: Date.now(),
                    ...(newBoardLinkedSubjectId ? { linkedSubjectId: newBoardLinkedSubjectId, linkedHallId: newBoardLinkedHallId } : {}),
                  });
                  setShowCreateBoard(false);
                  setNewBoardLinkedHallId('');
                  setNewBoardLinkedSubjectId('');
                }}
                className="bg-yellow-700 text-white px-4 py-1.5 rounded-lg text-[9px] font-black uppercase hover:bg-yellow-600"
              >Opret</button>
            </div>
          </div>
        </div>
      )}

      {(!showZoomPortal || zoomMinimized) && showSchedule && (
        <SchedulePopup roomId={room.id} classroomId={room.classroomId} classroomIds={room.classroomIds} accessCtx={accessCtx} currentUser={me} onClose={() => setShowSchedule(false)} onOpenForge={() => onOpenForge?.(room.id)} onNavigateToRoom={onNavigateToRoom} />
      )}

      {showOccupantList && (!showZoomPortal || zoomMinimized) && (
        <OccupantList occupants={occupants} classroomId={room.classroomId} classroomIds={room.classroomIds || (room.classroomId ? [room.classroomId] : [])} roomId={room.id} currentUser={me} accessCtx={accessCtx} onPlayerClick={(p) => onPlayerClick?.(p)} onClose={() => setShowOccupantList(false)} />
      )}

      {/* DAGBOG */}
      {showJournal && (!showZoomPortal || zoomMinimized) && (
        <DailyJournal
          me={me}
          roomId={room.id}
          roomName={s(room.name)}
          visitStart={visitStart}
          mode={journalMode}
          previewTier={previewTier}
          classroomId={room.classroomId}
          onClose={handleJournalClose}
        />
      )}
    </div>
  );
};

const AvatarWrapper: React.FC<{ player: Player, isMe: boolean, crowdFactor: number, onPlayerClick?: (player: Player) => void }> = ({ player, isMe, crowdFactor, onPlayerClick }) => {
  const depthScale = 0.7 + ((player.y - 65) / 30) * 0.7;
  const isGuardian = isStaff(player.role);
  const priorityZIndex = isMe || isGuardian ? 1000 : 0;

  return (
    <div
      className="absolute transition-all duration-700"
      style={{
        left: `${player.x}%`,
        top: `${player.y}%`,
        transform: `translate(-50%, -100%) scale(${depthScale * crowdFactor})`,
        zIndex: Math.floor(player.y) + priorityZIndex
      }}
    >
      <Avatar player={player} isMe={isMe} onClick={() => onPlayerClick?.(player)} />
    </div>
  );
};

export default Room;